import type { FC } from 'react';
import React from 'react';
import type { OptionProps, OptionTypeBase } from 'react-select';
import { components } from 'react-select';
import { useI18n } from '@whizz/react-i18n';
import { Selector } from '../../../components';
import type { YearGroupAllowNullOption, YearGroupOption } from '../../../util/types';
import type { FieldGroupProps } from '../../../components/formGroup/types';

const YearGroupSelector: FC<Props> = ({
  onChange,
  value,
  options,
  error,
  required = false,
  isClearable = false,
  disableNoYearGroup = false,
  hideLabel = false,
  placeholder,
  label,
  ...rest
}) => {
  const { translate } = useI18n();

  if (!disableNoYearGroup) {
    options = [
      {
        label: translate('global.NO_YEAR_GROUP'),
        value: null,
      },
      ...options,
    ];
  }

  const finalValue = options.find((option) => option.value === value);

  const customOption = (props: OptionProps<OptionTypeBase, false>) => {
    return (
      <components.Option {...props}>
        <div className={'flex flex-row-reverse justify-end items-center'}>
          <div className={'text-left'}>{props.children}</div>
        </div>
      </components.Option>
    );
  };

  return (
    <Selector
      id={'default_year_group'}
      label={label ?? translate('components.SELECTORS.YEAR_GROUP.LABEL')}
      options={options}
      value={finalValue}
      placeholder={placeholder ?? translate('components.SELECTORS.PLACEHOLDERS.YEAR_GROUP')}
      customComponents={{
        Option: customOption,
      }}
      onChange={onChange}
      error={error}
      required={required}
      isClearable={isClearable}
      isSearchable={false}
      hideLabel={hideLabel}
      {...rest}
    />
  );
};

export default YearGroupSelector;

interface Props extends Pick<FieldGroupProps, 'labelStyle'> {
  onChange: (selectedOption: YearGroupOption | YearGroupAllowNullOption) => void;
  value: number | '' | null;
  options: (YearGroupOption | YearGroupAllowNullOption)[];
  error?: string | null;
  required?: boolean;
  isClearable?: boolean;
  isDisabled?: boolean;
  hint?: string | null;
  label?: string;
  disableNoYearGroup?: boolean;
  hideLabel?: boolean;
  placeholder?: string;
}
